import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Toaster, toast } from "sonner";
import axios from "axios";
import { Crown, ShieldCheck, Landmark, ArrowUpRight, Globe2, Ban } from "lucide-react";
import { DirectorKYCStep } from "../components/director/DirectorKYCStep";
import { useAuth } from "../context/AuthContext";
import { redirectIfPhonePe } from "../lib/payRail";
import { PayRailButtons } from "../components/payments/PayRailButtons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API, withCredentials: true });

const COUNTRIES = [
  { code: "IN", label: "India", flag: "🇮🇳" },
  { code: "US", label: "United States", flag: "🇺🇸" },
  { code: "GB", label: "United Kingdom", flag: "🇬🇧" },
  { code: "AE", label: "United Arab Emirates", flag: "🇦🇪" },
  { code: "SG", label: "Singapore", flag: "🇸🇬" },
];

function loadRzp() {
  return new Promise((res) => {
    if (window.Razorpay) return res(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => res(true); s.onerror = () => res(false);
    document.body.appendChild(s);
  });
}

export default function DirectorApply() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [country, setCountry] = useState("IN");
  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "",
    business_name: "", state_or_region: "",
    din: "", pan: "",
    ssn_last4: "", ein: "", us_tax_id: "", driver_license: "", passport_no: "",
    bank_holder: "", bank_name: "", account_number: "",
    ifsc: "", routing_number: "",
  });
  const [applying, setApplying] = useState(false);
  const [paying, setPaying] = useState(false);
  const [status, setStatus] = useState(null);
  const [feeAmount, setFeeAmount] = useState(295000);
  const [searchParams] = useSearchParams();
  const feeOf = (d) => (d?.fee_total ?? d?.fee_amount ?? 295000);
  const inrIn = (n) => Number(n || 0).toLocaleString("en-IN");
  const inviteToken = searchParams.get("invite") || "";
  const [invite, setInvite] = useState(null);
  const [inviteError, setInviteError] = useState("");
  const [kycDeferred, setKycDeferred] = useState(false);

  useEffect(() => {
    if (!inviteToken) return;
    api.get(`/director/invites/${inviteToken}`)
      .then((r) => {
        setInvite(r.data);
        setForm((f) => ({ ...f, name: r.data.name || f.name, email: r.data.email || f.email }));
        if (r.data.country) setCountry(r.data.country);
      })
      .catch((err) => setInviteError(err.response?.data?.detail || "This invitation link is invalid or has expired."));
  }, [inviteToken]);

  useEffect(() => {
    if (user && user.role === "director") {
      api.get("/director/status").then((r) => {
        setStatus(r.data); setFeeAmount(feeOf(r.data));
        setCountry(r.data.country || "IN");
      }).catch(() => {});
    }
  }, [user]);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const apply = async (e) => {
    e.preventDefault(); setApplying(true);
    try {
      const payload = { ...form, country, ...(inviteToken ? { invite_token: inviteToken } : {}) };
      const { data } = await api.post("/director/apply", payload);
      toast.success("Application received. Please pay the Executive Board fee.");
      const st = (await api.get("/director/status").catch(() => null))?.data;
      setStatus(st || { director_status: "pending_payment", fee_amount: data.user?.fee_amount ?? 250000,
                    email: data.user?.email, name: form.name, id: data.user?.id, country });
      setFeeAmount(feeOf(st || data.user));
      await refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Application failed. Please retry."); }
    finally { setApplying(false); }
  };

  const pay = async (railChoice = "razorpay") => {
    setPaying(railChoice);
    try {
      const ok = await loadRzp();
      if (!ok) { toast.error("Could not load Razorpay. Retry."); setPaying(false); return; }
      const { data } = await api.post(`/director/pay/create-order?rail=${railChoice}`);
      if (redirectIfPhonePe(data)) return;
      const rz = new window.Razorpay({
        key: data.key_id, amount: data.amount_paise, currency: data.currency,
        name: "Vinci Inc. · Executive Board",
        description: `${status?.fee_slab_label || "Executive"} — Authorization Fee (incl. 18% GST)`,
        order_id: data.order_id,
        prefill: { name: data.name || "", email: data.email || "",
                    contact: (data.phone || "").replace(/^\+/, "") },
        theme: { color: "#C9A961" },
        handler: async (rp) => {
          try {
            await api.post("/director/pay/verify", {
              razorpay_order_id: rp.razorpay_order_id,
              razorpay_payment_id: rp.razorpay_payment_id,
              razorpay_signature: rp.razorpay_signature,
            });
            toast.success("Fee captured. Awaiting Admin authorization.");
            const s = (await api.get("/director/status")).data;
            setStatus(s); await refresh();
          } catch (err) { toast.error(err?.response?.data?.detail || "Verification failed"); }
        },
        modal: { ondismiss: () => setPaying(false) },
      });
      rz.open();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start payment.");
      setPaying(false);
    }
  };

  const c = COUNTRIES.find((x) => x.code === country) || COUNTRIES[0];

  const kyc = status?.kyc;
  const kycBlocks = status && status.director_status === "pending_payment" && kyc?.applies && kyc.status !== "verified"
    && (kyc.mode === "A" || !kycDeferred);
  if (kycBlocks) {
    return (
      <PresidentialShell>
        <DirectorKYCStep api={api} allowContinue={kyc.mode === "B"} onVerifiedOrContinue={async () => { setKycDeferred(true); try { setStatus((await api.get("/director/status")).data); } catch { /* keep */ } }} />
      </PresidentialShell>
    );
  }
  const kycBanner = status && kyc?.applies && kyc.status !== "verified" && kyc.mode === "B" && ["pending_payment", "pending_authorization"].includes(status.director_status) ? (
    <div data-testid="director-kyc-banner" className="mx-auto max-w-xl mb-8 rounded-sm border border-[#D8B23E]/50 bg-[#D8B23E]/5 px-5 py-3 text-center">
      <span className="font-grotesk text-[9px] uppercase tracking-[0.3em] text-[#D8B23E]">eKYC · {kyc.label}</span>
      <div className="font-body text-xs text-[#C9C9C2] mt-1">Full activation is held until your eKYC is approved.{kyc.status !== "pending_review" && <button onClick={() => setKycDeferred(false)} data-testid="director-kyc-open" className="ml-2 text-[#C9A961] underline">Complete eKYC</button>}</div>
    </div>) : null;

  if (status && status.director_status === "pending_payment" && (status.fee_waived || Number(status.fee_amount) === 0)) {
    return (
      <PresidentialShell>
        {kycBanner}
        <div className="max-w-2xl mx-auto text-center" data-testid="director-fee-waived">
          <ShieldCheck size={44} className="mx-auto text-[#C9A961]" />
          <span className="block mt-6 font-grotesk text-[10px] uppercase tracking-[0.35em] text-[#C9A961]">· {status.fee_slab_label || "Fee waived"} ·</span>
          <h1 className="font-serif text-4xl md:text-5xl mt-4">No fee payable — Board is reviewing</h1>
          <p className="font-body text-[#8A8A82] mt-6 leading-relaxed">{status.fee_note}</p>
          <p className="font-body text-[#8A8A82] mt-3 leading-relaxed">
            Vinci Admin will authorize your Executive account manually and you will be notified at <span className="text-[#C9A961]">{status.email}</span>.
          </p>
        </div>
      </PresidentialShell>
    );
  }

  if (status && status.director_status === "pending_payment") {
    return (
      <PresidentialShell>
        {kycBanner}
        <div className="max-w-2xl mx-auto text-center">
          <span className="inline-block font-grotesk text-[10px] uppercase tracking-[0.35em] text-[#C9A961] mb-6">
            · Step 2 of 2 · Authorization Fee
          </span>
          <h1 className="font-serif text-4xl md:text-6xl leading-tight text-[#F5F5F0]" data-testid="director-pay-heading">
            {status.fee_slab_label || "Executive Board"}
            <span className="text-[#C9A961]"> — Authorization Fee</span>
          </h1>
          <div data-testid="director-pay-slab" className="mt-6 inline-block border border-[#C9A961]/50 px-5 py-3 text-left">
            <span className="font-grotesk text-[9px] uppercase tracking-[0.3em] text-[#C9A961]">Fee structure · locked at invitation</span>
            <div className="font-serif text-lg text-[#F5F5F0] mt-1" data-testid="director-pay-fee-line">
              {status.fee_slab_label} Fee — INR {inrIn(status.fee_amount)} + 18% GST = INR {inrIn(status.fee_total ?? feeAmount)} total
            </div>
            <div className="font-body text-xs text-[#C9C9C2] mt-1">{status.fee_note}</div>
          </div>
          <p className="font-body text-[#8A8A82] mt-6 leading-relaxed">
            ₹{inrIn(feeAmount)} (inclusive of GST, SAC 998319) — captured over Razorpay or PhonePe, your choice.
            After payment, an Admin will manually authorize your Executive
            account. You'll receive a Board-signed confirmation email the
            moment your Presidential Portal unlocks.
          </p>
          <div className="mt-10 max-w-md" data-testid="director-pay-btn">
            <PayRailButtons testId="director-pay" onPay={pay} busy={paying} amountLabel={`₹${inrIn(feeAmount)}`} note="GST invoice issued on capture — on either rail." />
          </div>
        </div>
      </PresidentialShell>
    );
  }

  if (status && status.director_status === "pending_authorization" && kyc?.applies && kyc.status !== "verified" && kyc.status !== "pending_review" && !kycDeferred) {
    return (<PresidentialShell><DirectorKYCStep api={api} allowContinue onVerifiedOrContinue={() => setKycDeferred(true)} /></PresidentialShell>);
  }

  if (status && status.director_status === "pending_authorization") {
    return (
      <PresidentialShell>
        {kycBanner}
        <div className="max-w-2xl mx-auto text-center">
          <ShieldCheck size={44} className="mx-auto text-[#C9A961]" />
          <h1 className="font-serif text-4xl md:text-5xl mt-6">Fee received — Board is reviewing</h1>
          <p className="font-body text-[#8A8A82] mt-6 leading-relaxed">
            Your fee has been captured. Vinci Admin will authorize your
            Executive account manually. You will receive an email at
            <span className="text-[#C9A961]"> {status.email} </span>
            the moment access unlocks. Typical review window: 24 hours.
          </p>
          <button data-testid="director-pending-check" onClick={async () => {
            const s = (await api.get("/director/status")).data; setStatus(s);
            if (s.director_status === "active") nav("/director");
          }} className="mt-10 rounded-sm border border-[#C9A961]/50 px-7 py-3 font-grotesk text-xs uppercase tracking-[0.28em] text-[#C9A961] hover:bg-[#C9A961] hover:text-black transition-colors">
            Refresh status
          </button>
        </div>
      </PresidentialShell>
    );
  }

  if (invite?.used && !user) {
    return (
      <PresidentialShell>
        <div className="max-w-2xl mx-auto text-center" data-testid="director-invite-used">
          <ShieldCheck size={44} className="mx-auto text-[#C9A961]" />
          <span className="block mt-6 font-grotesk text-[10px] uppercase tracking-[0.35em] text-[#C9A961]">· Invitation already accepted ·</span>
          <h1 className="font-serif text-4xl md:text-5xl mt-4 text-[#F5F5F0]">Welcome back, {invite.name?.split(" ")[0]}</h1>
          <p className="font-body text-[#8A8A82] mt-6 leading-relaxed max-w-lg mx-auto">This invitation has already been used to open your Founders Suite account. Sign in to continue your onboarding and eKYC.</p>
          <Link to={`/login?next=${encodeURIComponent(`/director/kyc?invite=${inviteToken}`)}`} data-testid="director-invite-used-login" className="mt-10 inline-flex items-center gap-3 rounded-sm bg-[#C9A961] text-black px-8 py-3.5 font-grotesk text-xs uppercase tracking-[0.28em] hover:bg-[#E0C078] transition-colors">
            Sign in to continue
          </Link>
        </div>
      </PresidentialShell>
    );
  }

  if (inviteError) {
    return (
      <PresidentialShell>
        <div className="max-w-2xl mx-auto text-center" data-testid="director-invite-error">
          <Ban size={44} className="mx-auto text-[#E5484D]" />
          <span className="block mt-6 font-grotesk text-[10px] uppercase tracking-[0.35em] text-[#E5484D]">· Invitation unavailable ·</span>
          <h1 className="font-serif text-4xl md:text-5xl mt-4 text-[#F5F5F0]" data-testid="director-invite-error-text">{inviteError}</h1>
          <p className="font-body text-[#8A8A82] mt-6 leading-relaxed max-w-lg mx-auto">
            Private Founders Suite links are issued personally by the Vinci Board. If you believe this is in error, please write to the Trade Desk and a fresh invitation can be considered.
          </p>
          <Link to="/" data-testid="director-invite-error-home" className="mt-10 inline-flex items-center gap-3 rounded-sm border border-[#C9A961]/50 text-[#C9A961] px-8 py-3.5 font-grotesk text-xs uppercase tracking-[0.28em] hover:bg-[#C9A961] hover:text-black transition-colors">
            Return to Vinci Inc.
          </Link>
        </div>
      </PresidentialShell>
    );
  }

  if (status && status.director_status === "active") {
    return (
      <PresidentialShell>
        <div className="max-w-2xl mx-auto text-center">
          <Crown size={44} className="mx-auto text-[#C9A961]" />
          <h1 className="font-serif text-5xl mt-6">Executive Portal unlocked</h1>
          <Link to="/director" data-testid="director-enter-portal" className="mt-10 inline-flex items-center gap-3 rounded-sm bg-[#C9A961] text-black px-9 py-4 font-grotesk text-xs uppercase tracking-[0.28em] hover:bg-[#8B7333] hover:text-[#F5F5F0] transition-colors">
            Enter Board Portal <ArrowUpRight size={18} />
          </Link>
        </div>
      </PresidentialShell>
    );
  }

  return (
    <PresidentialShell>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} className="max-w-3xl mx-auto">
        <span className="block text-center font-grotesk text-[10px] uppercase tracking-[0.35em] text-[#C9A961] mb-6">
          · Level 0 · Executive Board Portal ·
        </span>
        <h1 className="font-serif text-center text-4xl md:text-6xl leading-tight text-[#F5F5F0]">
          Apply for the <span className="text-[#C9A961]">Founders Suite</span>
        </h1>
        <p className="font-body text-center text-[#8A8A82] mt-6 leading-relaxed max-w-xl mx-auto">
          Presidential visibility over the Vinci ecosystem — Master Ledger,
          Big-Cap League, custodian coverage across every state we serve. A
          one-time <span className="text-[#C9A961]">₹2,50,000</span> Executive
          Authorization Fee, captured via PhonePe or Razorpay. Every account is manually
          authorized by Admin.
        </p>

        {invite && (
          <div data-testid="director-invite-banner" className="mt-8 mx-auto max-w-xl rounded-sm border border-[#C9A961]/50 bg-[#C9A961]/5 p-5 text-center">
            <span className="font-grotesk text-[9px] uppercase tracking-[0.3em] text-[#C9A961]">Private invitation · issued by the Vinci Board</span>
            <div className="font-serif text-xl text-[#F5F5F0] mt-2">{invite.name}</div>
            <div className="font-body text-sm text-[#C9C9C2] mt-1">
              Cap-table stake locked at <span className="text-[#C9A961]">{Number(invite.preset_shareholding_pct).toFixed(2)}%</span>
            </div>
            <div data-testid="director-invite-fee-slab" className="mt-4 border-t border-[#C9A961]/30 pt-3">
              <span className="font-grotesk text-[9px] uppercase tracking-[0.3em] text-[#8A8A82]">Authorization Fee Structure · locked</span>
              <div className="font-serif text-lg text-[#F5F5F0] mt-1">{invite.fee_slab_label} — <span className="text-[#C9A961]">INR {inrIn(invite.fee_amount)}{invite.fee_amount ? ` + 18% GST = INR ${inrIn(invite.fee_total ?? Math.round(invite.fee_amount * 1.18))} total` : ""}</span></div>
              <div className="font-body text-xs text-[#C9C9C2] mt-1">{invite.fee_note}</div>
            </div>
            {invite.note && <p className="font-body text-xs italic text-[#8A8A82] mt-3">“{invite.note}”</p>}
          </div>
        )}

        {/* Country picker */}
        <div className="mt-10 mb-8">
          <span className="block text-center font-grotesk text-[10px] uppercase tracking-[0.3em] text-[#C9A961] mb-4">
            <Globe2 className="inline mr-2 -mt-1" size={14} /> Register from country
          </span>
          <div className="flex flex-wrap justify-center gap-2">
            {COUNTRIES.map((cc) => (
              <button
                key={cc.code}
                type="button"
                data-testid={`director-country-${cc.code}`}
                onClick={() => setCountry(cc.code)}
                className={`rounded-full px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em] transition-colors ${
                  country === cc.code ? "bg-[#C9A961] text-black" : "border border-[#C9A961]/40 text-[#C9A961] hover:bg-[#C9A961]/10"
                }`}
              >
                <span className="mr-1.5 text-base align-middle">{cc.flag}</span> {cc.label}
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={apply} data-testid="director-apply-form"
          className="grid md:grid-cols-2 gap-6 rounded-sm border border-[#C9A961]/25 bg-[#0f0d0a] p-8 md:p-10">
          <F l="Full Name *" testid="director-name">
            <input required name="name" value={form.name} onChange={onChange} className={INP} />
          </F>
          <F l="Business / Entity *" testid="director-business">
            <input required name="business_name" value={form.business_name} onChange={onChange} className={INP} />
          </F>
          <F l="Email *" testid="director-email">
            <input required type="email" name="email" value={form.email} onChange={onChange} readOnly={!!invite} data-testid="director-apply-email" className={`${INP} ${invite ? "opacity-70 cursor-not-allowed" : ""}`} />
          </F>
          <F l={country === "US" ? "US Mobile (10-digit) *" : "Mobile (10-digit) *"} testid="director-phone">
            <input required name="phone" value={form.phone} onChange={onChange} className={INP}
              placeholder={country === "US" ? "3055551234" : "9876543210"} />
          </F>
          <F l={country === "US" ? "State of Residence" : "State"} testid="director-state">
            <input name="state_or_region" value={form.state_or_region} onChange={onChange}
              className={INP} placeholder={country === "US" ? "Florida" : "Maharashtra"} />
          </F>

          {country === "IN" ? (
            <>
              <F l="DIN (Director Identification No.)" testid="director-din">
                <input name="din" value={form.din} onChange={onChange} className={INP} />
              </F>
              <F l="PAN" testid="director-pan">
                <input name="pan" value={form.pan} onChange={onChange} className={INP} placeholder="ABCPT1234K" />
              </F>
              <F l="Bank Account Holder" testid="director-bank-holder">
                <input name="bank_holder" value={form.bank_holder} onChange={onChange} className={INP} />
              </F>
              <F l="Bank Name" testid="director-bank-name">
                <input name="bank_name" value={form.bank_name} onChange={onChange} className={INP} />
              </F>
              <F l="Account Number" testid="director-account">
                <input name="account_number" value={form.account_number} onChange={onChange} className={INP} />
              </F>
              <F l="IFSC" testid="director-ifsc">
                <input name="ifsc" value={form.ifsc} onChange={onChange} className={INP} />
              </F>
            </>
          ) : country === "US" ? (
            <>
              <F l="EIN (Employer Identification No.)" testid="director-ein">
                <input name="ein" value={form.ein} onChange={onChange} className={INP} placeholder="12-3456789" />
              </F>
              <F l="SSN — last 4 digits only" testid="director-ssn">
                <input name="ssn_last4" value={form.ssn_last4} onChange={onChange} className={INP}
                  maxLength={4} placeholder="1234" />
              </F>
              <F l="US Tax ID / ITIN" testid="director-us-taxid">
                <input name="us_tax_id" value={form.us_tax_id} onChange={onChange} className={INP} />
              </F>
              <F l="Driver's License No." testid="director-dl">
                <input name="driver_license" value={form.driver_license} onChange={onChange} className={INP} />
              </F>
              <F l="US Passport No. (optional)" testid="director-passport">
                <input name="passport_no" value={form.passport_no} onChange={onChange} className={INP} />
              </F>
              <F l="Bank Account Holder" testid="director-bank-holder">
                <input name="bank_holder" value={form.bank_holder} onChange={onChange} className={INP} />
              </F>
              <F l="US Bank Name" testid="director-bank-name">
                <input name="bank_name" value={form.bank_name} onChange={onChange} className={INP} />
              </F>
              <F l="ABA Routing Number (9 digits)" testid="director-routing">
                <input name="routing_number" value={form.routing_number} onChange={onChange} className={INP} placeholder="123456789" />
              </F>
              <F l="Account Number" testid="director-account">
                <input name="account_number" value={form.account_number} onChange={onChange} className={INP} />
              </F>
            </>
          ) : (
            <>
              <F l="US Passport / National ID" testid="director-passport">
                <input name="passport_no" value={form.passport_no} onChange={onChange} className={INP} />
              </F>
              <F l="Tax ID (country-specific)" testid="director-us-taxid">
                <input name="us_tax_id" value={form.us_tax_id} onChange={onChange} className={INP} />
              </F>
            </>
          )}

          <F l="Password *" testid="director-password" full>
            <input required type="password" name="password" value={form.password} onChange={onChange} className={INP} placeholder="Min 8 characters" />
          </F>

          <div className="md:col-span-2 flex flex-col items-center gap-4 pt-4 border-t border-white/10">
            <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82]">
              Registering as <span className="text-[#C9A961]">{c.flag} {c.label}</span> Founder
            </span>
            <button type="submit" disabled={applying} data-testid="director-apply-submit"
              className="inline-flex items-center gap-3 rounded-sm bg-[#C9A961] text-black px-9 py-4 font-grotesk text-xs uppercase tracking-[0.28em] hover:bg-[#8B7333] hover:text-[#F5F5F0] transition-all disabled:opacity-60">
              {applying ? "Submitting…" : "Continue to Fee →"}
            </button>
            <p className="font-body text-xs text-[#8A8A82]">
              Already registered? <Link to="/login" className="text-[#C9A961] hover:underline">Sign in</Link>
            </p>
          </div>
        </form>
      </motion.div>
    </PresidentialShell>
  );
}

const INP = "w-full bg-transparent border-b border-[#C9A961]/25 focus:border-[#C9A961] outline-none py-2.5 mt-1 font-body text-[#F5F5F0] text-lg";
function F({ l, testid, full, children }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="font-grotesk text-[10px] uppercase tracking-[0.25em] text-[#C9A961]">{l}</span>
      <div data-testid={testid}>{children}</div>
    </label>
  );
}

export function PresidentialShell({ children, elite = false }) {
  return (
    <div className="min-h-screen bg-[#0a0a0a] relative overflow-hidden">
      <Toaster position="bottom-right" theme="dark" richColors />
      <div className="absolute inset-0 pointer-events-none">
        <div className={`absolute -top-40 -left-40 rounded-full blur-[120px] ${elite ? "w-[720px] h-[720px] opacity-30" : "w-[500px] h-[500px] opacity-25"}`} style={{ background: "radial-gradient(circle,#C9A961 0%,transparent 70%)" }} />
        <div className={`absolute -bottom-52 -right-52 rounded-full blur-[140px] ${elite ? "w-[820px] h-[820px] opacity-25" : "w-[600px] h-[600px] opacity-20"}`} style={{ background: "radial-gradient(circle,#8B7333 0%,transparent 70%)" }} />
        {elite && (
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full blur-[160px] opacity-10" style={{ background: "radial-gradient(ellipse,#F5D67A 0%,transparent 70%)" }} />
        )}
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2260%22 height=%2260%22><path d=%22M0 0h60v60H0z%22 fill=%22none%22/><path d=%22M30 0v60M0 30h60%22 stroke=%22%23C9A961%22 stroke-width=%220.3%22/></svg>')" }} />
      </div>
      <div className="relative z-10 px-6 md:px-12 py-16 md:py-24">
        <Link to="/" className="inline-flex items-center gap-3 mb-10">
          <Landmark size={20} className="text-[#C9A961]" />
          <span className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-[#C9A961]">Vinci Inc. · Board</span>
        </Link>
        {children}
      </div>
    </div>
  );
}
